import { Link } from 'react-router';
import { useState } from 'react';
import {
  Download,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Search,
  Sun,
  User as UserIcon,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth.ts';
import { useAppShellDrawer } from '@/hooks/useAppShellDrawer.ts';
import { config } from '@/config';
import { useTheme } from '@/hooks/useTheme.ts';
import { InstallDialog, useInstallPrompt } from '@/features/pwa';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { ContextSwitcher } from './ContextSwitcher.tsx';
import { AccountMenuSection } from './AccountMenuSection.tsx';
import { useHeldAccounts } from '@/hooks/useHeldAccounts.ts';
import { NotificationBell } from '@/components/notifications/NotificationBell.tsx';
import { ReportIssueButton } from '@/components/feedback/ReportIssueButton.tsx';

/**
 * TopBar — the global header rendered by AppShell.
 *
 * Layout (mobile, < md):
 *   [hamburger]  [page title — flex-grow]            [search 🔍] [bell 🔔] [profile]
 *
 * Layout (desktop, md+):
 *   [ContextSwitcher]   [search box — centered]   [help] [bell 🔔] [profile menu]
 *
 * The hamburger button is the only way to open the drawer on mobile;
 * it's hidden at md+ where the drawer is persistent. It uses the
 * AppShell context (`useAppShellDrawer`) so this component composes
 * cleanly without prop drilling.
 *
 * Search is a placeholder for the future cmd-K palette (deferred per
 * spec Q-11 — primitive installed, palette not surfaced yet).
 */
export interface TopBarProps {
  /** Title shown on mobile in place of the search box. Defaults to the app name. */
  mobileTitle?: string;
}

export function TopBar({ mobileTitle = config.appName }: TopBarProps) {
  const { user } = useAuth();
  const drawer = useAppShellDrawer();
  const { preference, setPreference } = useTheme();
  const { availability: installAvailability } = useInstallPrompt();
  const [installOpen, setInstallOpen] = useState(false);
  const { data: heldAccounts } = useHeldAccounts(!!user);
  const heldCount = heldAccounts?.length ?? 0;
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : '';

  return (
    <div className="h-14 flex items-center gap-2 px-3 md:px-4">
      <InstallDialog open={installOpen} onOpenChange={setInstallOpen} />
      {/* Hamburger — mobile only, triggers drawer. */}
      <button
        type="button"
        onClick={drawer?.openDrawer}
        className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-foreground hover:bg-accent"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Context switcher — desktop. On mobile it lives in the drawer
          (rendered there by SideNav in a future iteration). */}
      <div className="hidden md:block">
        <ContextSwitcher />
      </div>

      {/* Mobile page title — flex-grow to push trailing items right. */}
      <h2 className="md:hidden flex-1 text-base font-semibold truncate">{mobileTitle}</h2>

      {/* Search — desktop. Placeholder for cmd-K. */}
      <div className="hidden md:flex flex-1 max-w-md mx-auto">
        <button
          type="button"
          className="w-full inline-flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground rounded-md border border-border-subtle bg-background hover:bg-accent transition-colors"
          // The cmd-K palette is deferred per Q-11; this button is a
          // visual placeholder so the topbar isn't empty on desktop.
          aria-label="Search (coming soon)"
          disabled
        >
          <Search className="w-4 h-4" aria-hidden="true" />
          <span className="flex-1 text-left">Search devices, incidents, changes…</span>
          <kbd className="hidden lg:inline-block px-1.5 py-0.5 text-[10px] font-mono bg-muted text-muted-foreground rounded border border-border-subtle">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Mobile search trigger — icon-only. */}
      <button
        type="button"
        className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-md text-foreground hover:bg-accent"
        aria-label="Search (coming soon)"
        disabled
      >
        <Search className="w-5 h-5" />
      </button>

      {/* Notifications inbox bell — unread badge + dropdown (authed users only). */}
      {user && <NotificationBell />}

      {/* Report an issue — bug / feature-request widget (authed users only;
          hides itself in editions without the feature). */}
      {user && <ReportIssueButton />}

      <div className="hidden md:block w-px h-6 mx-1 bg-border-subtle" />

      {/* Profile menu. Anonymous users get sign-in / sign-up buttons. */}
      {user ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex h-11 w-11 md:h-8 md:w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold"
              aria-label={`Account menu for ${user.firstName} ${user.lastName}`}
            >
              {initials || <UserIcon className="w-4 h-4" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-52">
            <DropdownMenuLabel>
              <div className="text-sm font-medium">
                {user.firstName} {user.lastName}
              </div>
              <div className="text-xs text-muted-foreground font-normal truncate">
                {user.email}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <AccountMenuSection />
            <DropdownMenuItem asChild>
              <Link to="/account/profile" className="flex items-center gap-2 cursor-pointer">
                <UserIcon className="w-4 h-4" />
                My Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-normal">
              Theme
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={preference}
              onValueChange={(value) => {
                if (value === 'system' || value === 'light' || value === 'dark') {
                  setPreference(value);
                }
              }}
            >
              <DropdownMenuRadioItem value="system" className="gap-2">
                <Monitor className="w-4 h-4" />
                System
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="light" className="gap-2">
                <Sun className="w-4 h-4" />
                Light
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark" className="gap-2">
                <Moon className="w-4 h-4" />
                Dark
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            {/* App-level actions live together. Install sits here rather than in settings
                because it is a thing you do to the app, not a preference — and because
                nobody goes looking in Profile for it. Renders nothing where the browser
                cannot install, or where it already has. */}
            {installAvailability !== 'installed' && installAvailability !== 'unavailable' && (
              <DropdownMenuItem
                className="gap-2 cursor-pointer"
                onSelect={(event) => {
                  // Keep the dialog's opening click from also closing the menu out from
                  // under it, which unmounts the dialog before it renders.
                  event.preventDefault();
                  setInstallOpen(true);
                }}
              >
                <Download className="w-4 h-4" />
                Install app
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to="/signout" className="flex items-center gap-2 cursor-pointer">
                <LogOut className="w-4 h-4" />
                Log out
              </Link>
            </DropdownMenuItem>
            {heldCount > 1 && (
              <DropdownMenuItem asChild>
                <Link to="/signout?all=1" className="flex items-center gap-2 cursor-pointer">
                  <LogOut className="w-4 h-4" />
                  Sign out of all accounts
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <div className="flex items-center gap-1.5">
          <Link
            to="/signin"
            className="hidden md:inline-flex items-center px-3 py-1.5 text-sm font-medium text-foreground rounded-md hover:bg-accent"
          >
            Log in
          </Link>
          <Link
            to="/account/signup"
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-brand-hover"
          >
            Sign up
          </Link>
        </div>
      )}
    </div>
  );
}
